// src/rms/ads/rateCard.js
// ============================================================================
// Default rate card
// ============================================================================
// Mirrors the seed in 20260816140000_rms_direct_ad_sales.sql so the local
// backend has the same numbers as a real database. Edit both together, or move
// this to a generator like the vertical presets if it starts drifting.
//
// Pricing logic: rate follows the buyer's customer lifetime value, not the size
// of the audience. An equipment finance company closing one construction yard
// earns tens of thousands, so it can pay $25 CPM to reach a few hundred exactly
// qualified operators. A bike parts wholesaler cannot.
// ============================================================================

import db from "../data";

// [ad_category, sidebar_cpm, banner_cpm, cpc, floor_cpm, min_spend] in cents
const RATES = [
  ["construction_industrial", 2500, 3000, 24000, 1200, 250000],
  ["photography_av",          2200, 2600, 21000, 1000, 150000],
  ["self_storage",            2000, 2400, 19000, 1000, 150000],
  ["rv_travel",               1900, 2300, 17500,  900, 150000],
  ["marine",                  1800, 2200, 16500,  900, 150000],
  ["automotive",              1600, 2000, 14000,  800, 100000],
  ["powersports",             1600, 1900, 15000,  800, 100000],
  ["events_hospitality",      1400, 1700, 12000,  700, 100000],
  ["tools_hardware",          1300, 1600, 10500,  650, 100000],
  ["outdoor_recreation",      1200, 1500,  9200,  600,  75000],
  ["apparel_formalwear",      1100, 1400,  7800,  550,  75000],
  ["cycling",                 1000, 1300,  7000,  500,  75000],
];

// Two components mounting at once (the console's avails tab and its rate-card
// tab) both awaited the emptiness check before either had written, so both
// seeded and the table ended up with two of everything. Sharing one in-flight
// promise makes concurrent callers wait on the same seed rather than racing it.
let inFlight = null;

/**
 * Seeds the rate card if it is empty. Idempotent, and safe to call from several
 * components mounting simultaneously.
 */
export function ensureRateCard() {
  if (!inFlight) {
    inFlight = seed().catch((err) => {
      inFlight = null; // let a later caller retry after a failure
      throw err;
    });
  }
  return inFlight;
}

async function seed() {
  const existing = (await db.list("adRateCard", {})).data || [];
  if (existing.length) return existing;

  for (const [adCategory, sidebar, banner, cpc, floor, minSpend] of RATES) {
    await db.create("adRateCard", {
      ad_category: adCategory,
      placement: "sidebar",
      rack_cpm_cents: sidebar,
      floor_cpm_cents: floor,
      rack_cpc_cents: cpc,
      min_spend_cents: minSpend,
      is_active: true,
    });
    await db.create("adRateCard", {
      ad_category: adCategory,
      placement: "banner_top",
      rack_cpm_cents: banner,
      floor_cpm_cents: Math.round(floor * 1.2),
      rack_cpc_cents: Math.round(cpc * 1.1),
      min_spend_cents: minSpend,
      is_active: true,
    });
  }

  return (await db.list("adRateCard", {})).data || [];
}

export default ensureRateCard;
